import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, pluck, take } from 'rxjs/operators';
import { AccountService } from 'app/core/services/account.service';
import { Child, OneDriveModel, Sites } from 'app/core/models';

@Injectable({
  providedIn: 'root',
})
export class IntegrationsService {
  private _accountId: string;
  private _oneDriveUserData: BehaviorSubject<OneDriveModel | null>;

  constructor(private _http: HttpClient, private _accountService: AccountService) {
    this._accountService.currentAccount.pipe(pluck('id')).subscribe((accountId) => (this._accountId = accountId));
    this._oneDriveUserData = new BehaviorSubject(null);
  }

  private readonly ONEDRIVE_ENDPOINTS = {
    GET_SITES: '/sharepoint_sites',
    GET_CHILDREN: '/sharepoint_children',
    POST_SYNC: '/sharepoint_sync',
  };

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  public get oneDriveData$(): Observable<OneDriveModel> {
    return this._oneDriveUserData.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  /**
   * Responsible for fetching first level documents from user´s OneDrive repository.
   * @returns Observable of type OneDriveModel
   *
   * Top level 'sites' array from OneDriveModel returned object.
   */
  public loadOneDriveData(): Observable<Sites[]> {
    return this._http.get(`accounts/${this._accountId}${this.ONEDRIVE_ENDPOINTS.GET_SITES}`).pipe(
      take(1),
      pluck('sites'),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        return EMPTY;
      }),
    ) as Observable<Sites[]>;
  }

  /**
   * Responsible for fetching inner folder levels from user´s OneDrive repository.
   * @returns Observable of type Child
   *
   * An array of Child objects representing inner folders.
   */
  public loadODocumentsChildren(children: Child): Observable<Child> {
    return this._http
      .get<Child>(`accounts/${this._accountId}${this.ONEDRIVE_ENDPOINTS.GET_CHILDREN}`, {
        params: {
          drive_id: children.drive_id,
          item_id: children.item_id,
          site_id: children.site_id,
        },
      })
      .pipe(
        take(1),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return EMPTY;
        }),
      );
  }

  /**
   * Responsible for signaling the backend that the top first layer folder was chosen to sync.
   * @returns nothing.
   */
  public syncOneDriveSingleOption(data: Child | { one_drive: boolean }): Observable<void> {
    return this._http.post<void>(`accounts/${this._accountId}${this.ONEDRIVE_ENDPOINTS.POST_SYNC}`, data).pipe(
      take(1),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        return EMPTY;
      }),
    );
  }
}
